import { z } from 'zod';

/**
 * Wire contract for the platform-api `/platform/agent` routes (FEAT-1698).
 *
 * The SSE event names and JSON payload shapes mirror
 * `backend/node/packages/api/src/agent/events.ts` and the `AgentThreadPort` records exposed by
 * `GET /platform/agent/threads` and `GET /platform/agent/threads/:threadId`. Raw payloads are parsed once here into
 * discriminated unions so the hook and UI never re-guard loose values.
 */

/** Protocol-defined SSE event names emitted by the backend (lowercase by wire contract). */
const WIRE_EVENT_MESSAGE_DELTA = 'message_delta';
const WIRE_EVENT_TOOL_CALL_STARTED = 'tool_call_started';
const WIRE_EVENT_TOOL_CALL_RESULT = 'tool_call_result';
const WIRE_EVENT_TURN_COMPLETE = 'turn_complete';
const WIRE_EVENT_ERROR = 'error';

/** Internal discriminants for parsed agent stream events. */
export const AGENT_STREAM_EVENT = {
  MESSAGE_DELTA: 'MESSAGE_DELTA',
  TOOL_CALL_STARTED: 'TOOL_CALL_STARTED',
  TOOL_CALL_RESULT: 'TOOL_CALL_RESULT',
  TURN_COMPLETE: 'TURN_COMPLETE',
  ERROR: 'ERROR',
} as const;

/** Discriminant union of the parsed stream event types. */
export type AgentStreamEventType = (typeof AGENT_STREAM_EVENT)[keyof typeof AGENT_STREAM_EVENT];

/** A single parsed agent stream event; every payload field is already narrowed. */
export type AgentStreamEvent =
  | { type: typeof AGENT_STREAM_EVENT.MESSAGE_DELTA; delta: string }
  | {
      type: typeof AGENT_STREAM_EVENT.TOOL_CALL_STARTED;
      toolCallId: string;
      toolName: string;
      input: ParsedJsonObject;
    }
  | {
      type: typeof AGENT_STREAM_EVENT.TOOL_CALL_RESULT;
      toolCallId: string;
      toolName: string;
      isError: boolean;
    }
  | { type: typeof AGENT_STREAM_EVENT.TURN_COMPLETE; stopReason: string }
  | { type: typeof AGENT_STREAM_EVENT.ERROR; code: string; message: string };

/** Wire event name to internal discriminant; unknown names are dropped by the parser. */
const WIRE_EVENT_TYPES = {
  [WIRE_EVENT_MESSAGE_DELTA]: AGENT_STREAM_EVENT.MESSAGE_DELTA,
  [WIRE_EVENT_TOOL_CALL_STARTED]: AGENT_STREAM_EVENT.TOOL_CALL_STARTED,
  [WIRE_EVENT_TOOL_CALL_RESULT]: AGENT_STREAM_EVENT.TOOL_CALL_RESULT,
  [WIRE_EVENT_TURN_COMPLETE]: AGENT_STREAM_EVENT.TURN_COMPLETE,
  [WIRE_EVENT_ERROR]: AGENT_STREAM_EVENT.ERROR,
} as const satisfies Record<string, AgentStreamEventType>;

const jsonObjectSchema = z.object({}).catchall(z.json());
export type ParsedJsonObject = z.infer<typeof jsonObjectSchema>;
export type ParsedJsonValue = z.infer<ReturnType<typeof z.json>>;

/** Parses one JSON value into an object before a consumer reads named fields. */
export function parseJsonObject(value: ParsedJsonValue): ParsedJsonObject | null {
  const result = jsonObjectSchema.safeParse(value);
  return result.success ? result.data : null;
}

const agentStreamPayloadSchema = jsonObjectSchema;
const agentErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().catch(''),
  }),
});

const agentThreadSummariesSchema = z.object({
  threads: z.array(
    z.object({
      external_thread_id: z.string().min(1),
      title: z.string().min(1).nullable().catch(null),
      updated_at: z.string().catch(''),
      last_accessed_at: z.string().catch(''),
    })
  ),
});

const agentThreadHistorySchema = z.object({
  messages: z.array(
    z.object({
      id: z.string().catch(''),
      kind: z.string(),
      sequence_number: z.number().finite().catch(0),
      content: jsonObjectSchema.catch({}),
    })
  ),
});

/** Narrows an unknown field to a string without casting, returning `''` for every other shape. */
const stringField = (source: ParsedJsonObject, key: string): string => {
  const value = source[key];
  return z.string().catch('').parse(value);
};

/**
 * Reads a string field from an unknown value.
 *
 * Returns `''` unless the value is a plain object holding a string at `key`, so consumers of loosely
 * typed persisted `content` maps never need a type assertion.
 */
export function readStringField(value: z.input<typeof jsonObjectSchema>, key: string): string {
  const parsed = jsonObjectSchema.safeParse(value);
  return parsed.success ? stringField(parsed.data, key) : '';
}

/**
 * Parses one SSE frame into an agent stream event.
 *
 * Returns `null` for frames the client cannot use — unknown event names, malformed JSON, or
 * payloads missing their required fields — so a forward-compatible backend never breaks the panel.
 */
export function parseAgentStreamEvent(eventName: string, rawData: string): AgentStreamEvent | null {
  const type = WIRE_EVENT_TYPES[eventName];
  if (!type) {
    return null;
  }

  let payload: ParsedJsonObject;
  try {
    payload = agentStreamPayloadSchema.parse(JSON.parse(rawData));
  } catch {
    return null;
  }

  switch (type) {
    case AGENT_STREAM_EVENT.MESSAGE_DELTA: {
      const delta = stringField(payload, 'delta');
      return delta.length > 0 ? { type, delta } : null;
    }
    case AGENT_STREAM_EVENT.TOOL_CALL_STARTED: {
      const toolCallId = stringField(payload, 'tool_call_id');
      const toolName = stringField(payload, 'tool_name');
      if (toolCallId.length === 0 || toolName.length === 0) {
        return null;
      }
      return {
        type,
        toolCallId,
        toolName,
        input: jsonObjectSchema.catch({}).parse(payload.input),
      };
    }
    case AGENT_STREAM_EVENT.TOOL_CALL_RESULT: {
      const toolCallId = stringField(payload, 'tool_call_id');
      if (toolCallId.length === 0) {
        return null;
      }
      return {
        type,
        toolCallId,
        toolName: stringField(payload, 'tool_name'),
        isError: payload.is_error === true,
      };
    }
    case AGENT_STREAM_EVENT.TURN_COMPLETE:
      return { type, stopReason: stringField(payload, 'stop_reason') };
    case AGENT_STREAM_EVENT.ERROR:
      return {
        type,
        code: stringField(payload, 'code'),
        message: stringField(payload, 'message'),
      };
    default:
      return null;
  }
}

/** The platform-api JSON error envelope, parsed from a failed `/platform/agent` response body. */
export type AgentErrorEnvelope = {
  code: string;
  message: string;
};

/**
 * Parses `{ error: { code, message } }` from a failed response body.
 *
 * Returns `null` when the body is not a platform-api envelope (proxy HTML, empty body, gateway
 * error) so callers fall back to status-derived reporting instead of inventing a code.
 */
export function parseAgentErrorEnvelope(payload: z.input<typeof jsonObjectSchema>): AgentErrorEnvelope | null {
  const parsed = agentErrorEnvelopeSchema.safeParse(payload);
  return parsed.success ? parsed.data.error : null;
}

/** Persisted message kinds returned by `GET /platform/agent/threads/:threadId`. */
export const AGENT_THREAD_MESSAGE_KIND = {
  USER: 'AGENT_THREAD_MESSAGE_KIND_USER',
  ASSISTANT: 'AGENT_THREAD_MESSAGE_KIND_ASSISTANT',
  TOOL_CALL: 'AGENT_THREAD_MESSAGE_KIND_TOOL_CALL',
  TOOL_RESULT: 'AGENT_THREAD_MESSAGE_KIND_TOOL_RESULT',
} as const;

/** Discriminant union of persisted agent thread message kinds. */
export type AgentThreadMessageKind = (typeof AGENT_THREAD_MESSAGE_KIND)[keyof typeof AGENT_THREAD_MESSAGE_KIND];

const AGENT_THREAD_MESSAGE_KINDS: readonly AgentThreadMessageKind[] = Object.values(AGENT_THREAD_MESSAGE_KIND);

/** Type guard for persisted message kinds, so parsing never casts an arbitrary string. */
const isAgentThreadMessageKind = (value: string): value is AgentThreadMessageKind =>
  AGENT_THREAD_MESSAGE_KINDS.some((kind) => kind === value);

/** One thread row from `GET /platform/agent/threads`. */
export type AgentThreadSummary = {
  externalThreadId: string;
  title: string | null;
  updatedAt: string;
  lastAccessedAt: string;
};

/** One persisted message row from `GET /platform/agent/threads/:threadId`. */
export type AgentThreadMessage = {
  id: string;
  kind: AgentThreadMessageKind;
  sequenceNumber: number;
  content: ParsedJsonObject;
};

/** Parses the `GET /platform/agent/threads` response, dropping rows without an external thread id. */
export function parseAgentThreadSummaries(payload: z.input<typeof jsonObjectSchema>): AgentThreadSummary[] {
  const parsed = agentThreadSummariesSchema.safeParse(payload);
  if (!parsed.success) {
    return [];
  }
  return parsed.data.threads.map((raw) => {
    const updatedAt = raw.updated_at;
    const lastAccessedAt = raw.last_accessed_at;
    return [
      {
        externalThreadId: raw.external_thread_id,
        title: raw.title,
        updatedAt,
        lastAccessedAt: lastAccessedAt.length > 0 ? lastAccessedAt : updatedAt,
      },
    ][0];
  });
}

/** Parses the `GET /platform/agent/threads/:threadId` response into ordered persisted messages. */
export function parseAgentThreadHistory(payload: z.input<typeof jsonObjectSchema>): AgentThreadMessage[] {
  const parsed = agentThreadHistorySchema.safeParse(payload);
  if (!parsed.success) {
    return [];
  }
  return parsed.data.messages
    .flatMap((raw) => {
      const kind = raw.kind;
      if (!isAgentThreadMessageKind(kind)) {
        return [];
      }
      return [
        {
          id: raw.id,
          kind,
          sequenceNumber: raw.sequence_number,
          content: raw.content,
        },
      ];
    })
    .sort((left, right) => left.sequenceNumber - right.sequenceNumber);
}
